import React, { useState } from "react";
import {
  Drawer,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  ListItemIcon,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import api from "../../api/api";

const EMPTY_COLUMNS = [];

const ColumnsModal = ({
  open,
  onClose,
  config = EMPTY_COLUMNS,
  onColumnsChange,
  tableKey = "rental_assets",
  onReload,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [columns, setColumns] = useState(config); // Локальное состояние для drag-and-drop

  // Обновляем локальное состояние при изменении config
  React.useEffect(() => {
    setColumns(config);
  }, [config]);

  // Перемещение колонки вверх
  const moveColumnUp = async (index) => {
    if (index === 0) return; // Уже первая
    
    const newColumns = [...columns];
    // Меняем местами текущую и предыдущую
    [newColumns[index - 1], newColumns[index]] = [newColumns[index], newColumns[index - 1]];
    
    // Обновляем order_index для всех колонок
    const updatedColumns = newColumns.map((col, idx) => ({
      ...col,
      order_index: idx + 1,
    }));

    setColumns(updatedColumns);
    await saveColumnOrder(updatedColumns);
  };

  // Перемещение колонки вниз
  const moveColumnDown = async (index) => {
    if (index === columns.length - 1) return; // Уже последняя
    
    const newColumns = [...columns];
    // Меняем местами текущую и следующую
    [newColumns[index], newColumns[index + 1]] = [newColumns[index + 1], newColumns[index]];
    
    // Обновляем order_index для всех колонок
    const updatedColumns = newColumns.map((col, idx) => ({
      ...col,
      order_index: idx + 1,
    }));

    setColumns(updatedColumns);
    await saveColumnOrder(updatedColumns);
  };

  // Сохранение порядка колонок
  const saveColumnOrder = async (updatedColumns) => {
    try {
      // Сохраняем новый порядок на сервере только для колонок с валидными числовыми ID
      for (const column of updatedColumns) {
        if (column.id && Number.isInteger(column.id) && column.id > 0) {
          await api.put(
            `/table-columns/${column.id}`,
            {
              order_index: column.order_index,
              is_visible: column.is_visible,
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
        } else {
          console.warn("Колонка не имеет валидного числового ID, пропускаем сохранение:", column);
        }
      }

      // Уведомляем родительский компонент об изменениях
      onColumnsChange(updatedColumns);
    } catch (error) {
      console.error("Ошибка сохранения порядка колонок:", error);
      // В случае ошибки возвращаем предыдущий порядок
      setColumns(config);
    }
  };

  // Обработка изменения видимости колонки
  const handleColumnVisibilityChange = async (columnIndex, checked) => {
    const updatedColumns = columns.map((column, index) =>
      index === columnIndex ? { ...column, is_visible: checked } : column);
    setColumns(updatedColumns);

    try {
      // Сохраняем изменение на сервере только если у колонки есть валидный числовой ID
      const column = updatedColumns[columnIndex];
      if (column.id && Number.isInteger(column.id) && column.id > 0) {
        await api.put(
          `/table-columns/${column.id}`,
          {
            is_visible: column.is_visible,
            order_index: column.order_index,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } else {
        console.warn("Колонка не имеет валидного числового ID, пропускаем сохранение:", column);
      }

      // Уведомляем родительский компонент
      onColumnsChange(updatedColumns);
    } catch (error) {
      console.error("Ошибка сохранения видимости колонки:", error);
      // В случае ошибки возвращаем предыдущее состояние
      setColumns(columns);
    }
  };

  // Функция для получения красивого названия колонки
  const getColumnDisplayName = (column) => {
    // Специальные переводы для конкретных колонок
    const translations = {
      display_name: "Полное имя",
      contact_info: "Контакты",
      black_list: "Черный список",
      client_type: "Тип клиента",
      first_name: "Имя",
      last_name: "Фамилия",
      company_id: "Компания",
      // Новые переводы для полей активов
      asset_number: "Номер актива",
      name: "Название",
      status: "Статус",
      deposit: "Депозит",
      purchase_price: "Цена покупки",
      purchase_date: "Дата покупки",
      total_revenue: "Общий доход",
      rental_count: "Количество аренд",
      last_rental_date: "Дата последней аренды",
      profitability_date: "Дата окупаемости",
      maintenance_date: "Дата обслуживания",
      client_number: "Номер клиента",
      debt: "Долг",
      source: "Источник привлечения",
      created_at: "Дата создания",
      telegram_id: "ID Telegram",
      additional_phone: "Дополнительный телефон",
      email: "Email",
      total_rental_amount: "Сумма аренд",
      total_deals_count: "Кол-во сделок",
      images: "Изображения",
      is_deleted: "Удален",
      deleted_at: "Дата удаления",
      specifications: "Спецификации",
      search_metadata: "Метаданные поиска",
      ai_tags: "AI теги",
      ai_updated_at: "Дата обновления AI",
      created_at: "Дата создания",
      updated_at: "Дата обновления",
      rental_point: "Пункт проката",
      rental_point_name: "Пункт проката",
      "rental_point.name": "Пункт проката",
    };

    // Если есть специальный перевод - используем его
    if (translations[column.field || column.column_name]) {
      return translations[column.field || column.column_name];
    }

    // Иначе используем label из базы данных
    return column.label;
  };

  // Функция автогенерации колонок
  const handleGenerateColumns = async () => {
    setIsGenerating(true);
    setGenerationStatus(null);

    try {
      console.log(`🔧 Генерация колонок для ${tableKey}...`);

      // Запрос на генерацию колонок
      const response = await api.post(
        `/table-columns/generate-configs/${tableKey}`
      );

      console.log("✅ Колонки сгенерированы:", response.data);

      setGenerationStatus({
        type: "success",
        message: `Сгенерировано ${response.data.configs_count} колонок для ${tableKey}`,
      });

      // Принудительно перезагружаем колонки из базы данных
      if (onReload) {
        setTimeout(() => {
          onReload();
        }, 1000); // Даем время серверу обработать данные
      }

      // Обновляем конфигурацию колонок в родительском компоненте
      if (onColumnsChange && response.data.configs) {
        // Преобразуем колонки в нужный формат
        const newColumns = response.data.configs.map((col) => ({
          id: col.id,
          table_key: tableKey,
          column_name: col.column_name,
          field: col.column_name,
          label: col.label,
          sortable: col.sortable,
          is_visible: col.is_visible,
        }));

        onColumnsChange(newColumns);
      }
    } catch (error) {
      console.error("❌ Ошибка генерации колонок:", error);
      setGenerationStatus({
        type: "error",
        message: `Ошибка генерации колонок: ${
          error.response?.data?.detail || error.message
        }`,
      });
    } finally {
      setIsGenerating(false);
    }
  };
  const handleColumnToggle = async (field) => {
    try {
      // Обновляем состояние видимости для каждой колонки
      const newColumns = config.map((col) =>
        col.field === field ? { ...col, is_visible: !col.is_visible } : col
      );

      // Отправляем изменения на сервер
      const updates = newColumns.map((col) => ({
        id: col.id,
        is_visible: col.is_visible,
      }));

      console.log("🔧 Сохраняем настройки колонок:", updates);

      const response = await api.put(
        "/table-columns/bulk-update-visibility",
        updates,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log("✅ Настройки колонок сохранены:", response.data);

      // Обновляем локальное состояние только после успешного сохранения
      onColumnsChange(newColumns);
    } catch (error) {
      console.error("❌ Ошибка сохранения настроек колонок:", error);
      alert(
        `Ошибка сохранения настроек: ${
          error.response?.data?.detail || error.message
        }`
      );
    }
  };
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: "350px",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {" "}
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontSize: "1.1rem", marginBottom: 1 }}
      >
        Настройка колонок
      </Typography>
      <Divider sx={{ marginBottom: 1.5 }} />
      {/* Статус генерации */}
      {generationStatus && (
        <Alert
          severity={generationStatus.type}
          sx={{ marginBottom: 1.5, fontSize: "0.85rem" }}
          onClose={() => setGenerationStatus(null)}
        >
          {generationStatus.message}
        </Alert>
      )}
      {/* Прокручиваемый контейнер */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          maxHeight: "calc(100vh - 200px)",
          paddingRight: "8px",
          marginRight: "-8px",
        }}
      >
        {columns.length > 0 ? (
          <List dense sx={{ padding: 0 }}>
            {columns.map((col, index) => (
              <ListItem 
                key={col.id || col.field}
                sx={{ 
                  padding: "4px 0",
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  }
                }}
              >
                {/* Кнопки для перемещения */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  minWidth: '32px',
                  marginRight: '8px'
                }}>
                  <IconButton
                    size="small"
                    onClick={() => moveColumnUp(index)}
                    disabled={index === 0}
                    sx={{ 
                      padding: '2px',
                      '&:disabled': { opacity: 0.3 }
                    }}
                  >
                    <KeyboardArrowUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveColumnDown(index)}
                    disabled={index === columns.length - 1}
                    sx={{ 
                      padding: '2px',
                      '&:disabled': { opacity: 0.3 }
                    }}
                  >
                    <KeyboardArrowDownIcon fontSize="small" />
                  </IconButton>
                </Box>
                
                {/* Позиция колонки */}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    minWidth: '24px',
                    fontSize: '0.75rem',
                    color: '#666',
                    marginRight: '8px',
                    textAlign: 'center'
                  }}
                >
                  {index + 1}
                </Typography>

                {/* Чекбокс и название колонки */}
                <FormControlLabel
                  sx={{
                    margin: 0,
                    flex: 1,
                    "& .MuiTypography-root": {
                      fontSize: "0.9rem",
                      lineHeight: 1.3,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={col.is_visible}
                      onChange={(e) => handleColumnVisibilityChange(index, e.target.checked)}
                    />
                  }
                  label={getColumnDisplayName(col)}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ textAlign: "center", padding: 2 }}>
            <Typography
              color="textSecondary"
              gutterBottom
              sx={{ fontSize: "0.9rem" }}
            >
              Нет доступных колонок для настройки
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ marginBottom: 1.5, fontSize: "0.8rem" }}
            >
              Вы можете автоматически сгенерировать конфигурацию колонок на
              основе модели данных
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={
                isGenerating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={handleGenerateColumns}
              disabled={isGenerating}
              sx={{ marginTop: 1 }}
            >
              {isGenerating ? "Генерация..." : "Сгенерировать колонки"}
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ColumnsModal;
